import * as Sentry from "@sentry/react";

declare global {
  interface Window {
    __preSentryInitErrors?: Event[];
    __removePreSentryInitErrorListener?: () => void;
  }
}

/**
 * Reads errors queued by the listener installed in index.html before Sentry
 * initialised.
 *
 * This exists to catch things like a failed <link> stylesheet loading error
 * or an uncaught script error, before the app's JS bundle runs. Actual
 * errors are reported as-is, preserving their stack trace; resource loading
 * failures arrive as a plain `Event` with no stack, so a synthetic Error is
 * built to describe them.
 *
 * Calling this function tears down the listener. Leaving it running would
 * grow `__preSentryInitErrors` unbounded for the rest of the session.
 */
export function reportPreSentryInitErrors(
  networkLoggingEnabled: boolean = false
) {
  try {
    (window.__preSentryInitErrors ?? []).forEach((entry) => {
      if (entry instanceof ErrorEvent) {
        // Plain errors
        Sentry.captureException(entry.error);
      } else {
        // Events
        if (entry.target instanceof HTMLLinkElement) {
          // <link> load errors
          if (networkLoggingEnabled) {
            Sentry.captureException(
              new Error(`Link load error: ${entry.type} ${entry.target.href}`)
            );
          }
        } else if (entry.target instanceof HTMLScriptElement) {
          // <script> load errors
          if (networkLoggingEnabled) {
            Sentry.captureException(
              new Error(`Script load error: ${entry.type} ${entry.target.src}`)
            );
          }
        } else {
          // Other Events
          Sentry.captureException(
            new Error(`Event raised pre Sentry init: ${eventMessage(entry)}`)
          );
        }
      }
    });
  } catch (e) {
    console.error("Failed to report pre-Sentry init errors", e);
  } finally {
    window.__removePreSentryInitErrorListener?.();
  }
}

function eventMessage(event: Event): string {
  const msgParts = [`event.type: ${event.type}`];

  const target = event.target;
  if (target instanceof Window) {
    msgParts.push("target: window");
  } else if (target instanceof Node) {
    const nodeName = target.nodeName.toLowerCase();
    msgParts.push(`target: ${nodeName}`);
  }

  return msgParts.join(", ");
}
